import type { ApiHelper, ApiResponseWithData, BaseRequest } from '..';
import { ApiHelperWithData, splitParams } from '..';
import type {
  MstBzppBulkUploadResponse,
  MstBzppMultiInsertParams,
  MstBzppCreateParams,
  MstBzppDetailResponse,
  MstBzppDialogItem,
  MstBzppSearchItem,
  MstBzppSearchParams,
  MstBzppUpdateParams,
  PagerData,
} from '../model';

export class MstBzppApi {
  private withData: ApiHelperWithData;
  constructor(private helper: ApiHelper) {
    this.withData = new ApiHelperWithData(helper);
  }

  /**
   * 사업자 정보를 등록한다.
   */
  create = (
    params: MstBzppCreateParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/api/mst-bzpp/create';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 사업자 정보를 수정한다.
   */
  update = (
    params: MstBzppUpdateParams & BaseRequest,
  ): Promise<ApiResponseWithData<{ result: number }>> => {
    const url = '/api/mst-bzpp/update';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   * 사업자 정보를 삭제한다.
   */
  delete = (
    params: { coId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<number | { result: number }>> => {
    const url = '/api/mst-bzpp/delete';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /**
   *  발주사 검색 다이얼로그의 목록을 조회한다.
   *  ClientSearchDialog에서 사용한다.
   */
  dialogList = (
    params: { keyword?: string } & BaseRequest,
  ): Promise<ApiResponseWithData<{ list: MstBzppDialogItem[] }>> => {
    const url = '/apis/bzpp/dialog-list';
    return this.withData.postJson(url, ...splitParams(params));
  };
  /**
   * 사업자 정보를 조회한다.
   */
  search = (
    params: MstBzppSearchParams & BaseRequest,
  ): Promise<
    ApiResponseWithData<{ pagerData?: PagerData<MstBzppSearchItem> | MstBzppSearchItem[] }>
  > => {
    const url = '/api/mst-bzpp/search';
    return this.withData.postJson(url, ...splitParams(params));
  };
  /**
   * 사업자 상세정보를 가져온다.
   */
  detail = (
    params: { coId: string } & BaseRequest,
  ): Promise<
    ApiResponseWithData<{
      detail: MstBzppDetailResponse;
    }>
  > => {
    const url = '/api/mst-bzpp/detail';
    return this.withData.postJson(url, ...splitParams(params));
  };
  /**
   * 다건 등록
   */
  multiInsert = (
    params: MstBzppMultiInsertParams & BaseRequest,
  ): Promise<
    ApiResponseWithData<{
      detail: MstBzppDetailResponse;
    }>
  > => {
    const url = '/api/mst-bzpp/multiInsert';
    return this.withData.postJson(url, ...splitParams(params));
  };

  /** 엑셀 파일을 BZPP_UPLOAD_TMP에 임시 적재하고 검증 결과를 조회한다. */
  bulkUpload = (
    params: { file: Blob; fileName: string } & BaseRequest,
  ): Promise<ApiResponseWithData<MstBzppBulkUploadResponse>> => {
    const [data, extra] = splitParams(params);
    const formData = new FormData();
    formData.append('file', data.file, data.fileName);

    const url = '/api/bzpp/bulk-upload';
    return this.withData.postMultipart(url, formData, extra);
  };

  /** BATCH ID로 이전 임시 적재 결과를 다시 조회한다. */
  bulkUploadResult = (
    params: { batchId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<MstBzppBulkUploadResponse>> => {
    const [data, extra] = splitParams(params);
    const batchId = encodeURIComponent(data.batchId);
    const url = `/api/bzpp/bulk-upload/${batchId}`;

    return this.withData.get(url, undefined, extra);
  };

  /** 검증이 끝난 BATCH 데이터를 실제 BZPP 테이블에 최종 반영한다. */
  applyBulkUpload = (
    params: { batchId: string } & BaseRequest,
  ): Promise<ApiResponseWithData<MstBzppBulkUploadResponse>> => {
    const [data, extra] = splitParams(params);
    const batchId = encodeURIComponent(data.batchId);
    const url = `/api/bzpp/bulk-upload/${batchId}/apply`;

    return this.withData.postJson(url, {}, extra);
  };
}
